import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { inr, formatApiErrorDetail, imgUrl, roomGstRate, gstPct, CONVENIENCE_FEE, CONVENIENCE_FEE_GST, CONVENIENCE_FEE_GST_RATE, OTA_SAVING_RATE, isWholeUnit } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { CheckoutAuthDialog } from "@/components/CheckoutAuthDialog";
import { toast } from "sonner";
import { BadgePercent, Calendar as CalIcon, Minus, Plus } from "lucide-react";

const FALLBACK = "https://images.pexels.com/photos/237371/pexels-photo-237371.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function GuestRow({ title, subtitle, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <div className="text-sm font-semibold text-[#222]">{title}</div>
        <div className="text-xs text-[#717171]">{subtitle}</div>
      </div>
      <div className="flex items-center gap-3">
        <button type="button" data-testid={`${testid}-minus`} onClick={() => onChange(value - 1)} disabled={value <= 0} className="w-8 h-8 rounded-full border border-[#B0B0B0] flex items-center justify-center disabled:opacity-40 hover:border-[#222] transition-colors"><Minus className="w-4 h-4" /></button>
        <span data-testid={`${testid}-count`} className="w-5 text-center text-sm font-medium">{value}</span>
        <button type="button" data-testid={`${testid}-plus`} onClick={() => onChange(value + 1)} className="w-8 h-8 rounded-full border border-[#B0B0B0] flex items-center justify-center hover:border-[#222] transition-colors"><Plus className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export default function Booking() {
  const { hotelId, roomId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState(null);
  const [room, setRoom] = useState(null);
  const [today] = useState(() => new Date().toISOString().slice(0, 10));
  const [tomorrow] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10);
  });
  const [checkIn, setCheckIn] = useState(today);
  const [checkOut, setCheckOut] = useState(tomorrow);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [guestName, setGuestName] = useState(user?.name || "");
  const [guestEmail, setGuestEmail] = useState(user?.email || "");
  const [guestPhone, setGuestPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const draftKey = `booking_draft_${hotelId}_${roomId}`;

  // Restore a saved draft (e.g. after returning from Google login)
  useEffect(() => {
    const saved = sessionStorage.getItem(draftKey);
    if (!saved) return;
    try {
      const s = JSON.parse(saved);
      if (s.checkIn) setCheckIn(s.checkIn);
      if (s.checkOut) setCheckOut(s.checkOut);
      if (typeof s.adults === "number") setAdults(s.adults);
      if (typeof s.children === "number") setChildren(s.children);
      if (typeof s.infants === "number") setInfants(s.infants);
      if (s.guestName) setGuestName(s.guestName);
      if (s.guestEmail) setGuestEmail(s.guestEmail);
      if (s.guestPhone) setGuestPhone(s.guestPhone);
    } catch { /* ignore */ }
  }, [draftKey]);

  // Prefill guest name/email once the user is known
  useEffect(() => {
    if (!user) return;
    setGuestName((v) => v || user.name || "");
    setGuestEmail((v) => v || user.email || "");
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/hotels/${hotelId}`);
        setHotel(data.hotel);
        setRoom(data.rooms.find((r) => r.id === roomId) || null);
      } catch (err) {
        console.error("Failed to load booking data:", err);
      }
    })();
  }, [hotelId, roomId]);

  const wholeUnit = isWholeUnit(hotel?.category);
  const perUnitGuests = room?.max_guests || room?.capacity || 2;
  const inventory = room?.number_of_rooms || 1;
  const maxAdults = wholeUnit ? perUnitGuests : perUnitGuests * inventory;
  // Only adults (8+) are chargeable; children (<8) & infants ride free.
  const units = wholeUnit ? 1 : Math.max(1, Math.ceil(Math.max(1, adults) / perUnitGuests));

  const nights = Math.max(1, Math.round((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)));
  const price = room?.price_per_night || 0;
  const roomTotal = price * nights * units;
  const gstRate = roomGstRate(price);
  const gstAmount = Math.round(roomTotal * gstRate);
  const total = roomTotal + gstAmount + CONVENIENCE_FEE + CONVENIENCE_FEE_GST;
  const otaSaving = Math.round(roomTotal * OTA_SAVING_RATE);

  // Keep adults within allowed range
  useEffect(() => {
    if (adults > maxAdults) setAdults(maxAdults);
  }, [maxAdults]); // eslint-disable-line

  const doCreateBooking = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        hotel_id: hotelId, room_id: roomId,
        check_in: checkIn, check_out: checkOut,
        adults, children, infants, guests: adults + children, rooms_booked: units,
        guest_name: guestName, guest_email: guestEmail, guest_phone: guestPhone,
      });
      sessionStorage.removeItem(draftKey);
      navigate(`/payment/${data.booking.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!user) {
      // Save progress, then prompt login/signup at checkout instead of bouncing to /login
      sessionStorage.setItem(draftKey, JSON.stringify({ checkIn, checkOut, adults, children, infants, guestName, guestEmail, guestPhone }));
      setAuthOpen(true);
      return;
    }
    doCreateBooking();
  };

  if (!hotel || !room) return <div className="py-20 text-center text-[#717171]">Loading...</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-display font-bold mb-6">Review your reservation</h1>
      <div className="grid lg:grid-cols-5 gap-8">
        <form onSubmit={submit} className="lg:col-span-3 space-y-6">
          <div className="bg-white border border-[#EBEBEB] rounded-2xl p-6">
            <h2 className="font-display font-semibold text-lg mb-4">Your trip</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm mb-1 block">Check in</Label>
                <Input data-testid="booking-checkin-input" type="date" value={checkIn} min={today} onChange={(e) => setCheckIn(e.target.value)} className="rounded-xl" />
              </div>
              <div>
                <Label className="text-sm mb-1 block">Check out</Label>
                <Input data-testid="booking-checkout-input" type="date" value={checkOut} min={checkIn} onChange={(e) => setCheckOut(e.target.value)} className="rounded-xl" />
              </div>
            </div>
            <div className="mt-4 divide-y divide-[#EBEBEB] border border-[#EBEBEB] rounded-xl px-4">
              <GuestRow title="Adults" subtitle="Ages 8 years above · charged" value={adults} onChange={(v) => setAdults(Math.min(maxAdults, Math.max(0, v)))} testid="booking-adults" />
              <GuestRow title="Children" subtitle="Ages 3–8 · stay free" value={children} onChange={(v) => setChildren(Math.max(0, v))} testid="booking-children" />
              <GuestRow title="Infants" subtitle="Under 3 · stay free" value={infants} onChange={(v) => setInfants(Math.max(0, v))} testid="booking-infants" />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[#717171]">
              <span data-testid="booking-charged-note">Only adults (8+) are charged — children under 8 stay free.</span>
              {!wholeUnit ? (
                <span data-testid="booking-rooms-auto" className="font-medium text-[#222]">{units} room{units > 1 ? "s" : ""} for {adults} adult{adults > 1 ? "s" : ""} ({perUnitGuests}/room · {inventory} available)</span>
              ) : (
                <span className="font-medium text-[#222]">Entire place · up to {maxAdults} guests</span>
              )}
            </div>
          </div>

          <div className="bg-white border border-[#EBEBEB] rounded-2xl p-6">
            <h2 className="font-display font-semibold text-lg mb-4">Guest details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-sm mb-1 block">Full name</Label>
                <Input data-testid="booking-name-input" required value={guestName} onChange={(e) => setGuestName(e.target.value)} className="rounded-xl" />
              </div>
              <div>
                <Label className="text-sm mb-1 block">Email</Label>
                <Input data-testid="booking-email-input" type="email" required value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} className="rounded-xl" />
              </div>
              <div>
                <Label className="text-sm mb-1 block">Phone</Label>
                <Input data-testid="booking-phone-input" required value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} placeholder="+91..." className="rounded-xl" />
              </div>
            </div>
            <Button
              data-testid="booking-pay-btn"
              type="submit"
              disabled={submitting}
              className="w-full mt-6 rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white h-12 text-base font-semibold"
            >
              {submitting ? "Processing..." : (user ? `Pay now · ${inr(total)}` : `Continue · ${inr(total)}`)}
            </Button>
            {!user && (
              <p className="text-xs text-center text-[#717171] mt-2" data-testid="booking-login-hint">You'll be asked to log in or sign up before payment — your details are saved.</p>
            )}
          </div>
        </form>

        <aside className="lg:col-span-2">
          <div className="bg-white border border-[#EBEBEB] rounded-2xl p-6 sticky top-28">
            <img src={imgUrl(room.images?.[0] || hotel.images?.[0]) || FALLBACK} alt={room.name} className="w-full aspect-[4/3] object-cover rounded-xl mb-4" onError={(e) => { e.currentTarget.src = FALLBACK; }} />
            <h3 className="font-display font-semibold text-lg">{hotel.name}</h3>
            <p className="text-sm text-[#717171] mb-1">{wholeUnit ? `Entire ${(hotel.category || "place")} · up to ${maxAdults} guests` : `${room.name} · ${units} room(s) · ${adults} adult(s)${children ? ` + ${children} child(ren) free` : ""}`}</p>
            <p className="text-xs text-[#717171] mb-4">{hotel.city}, {hotel.country}</p>
            <hr className="border-[#EBEBEB] my-4" />
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Total price of reservation</span><span data-testid="bd-reservation">{inr(roomTotal)}</span></div>
              <div className="flex justify-between text-[#717171]"><span>{inr(price)} × {nights} night(s){wholeUnit ? "" : ` × ${units} room(s)`}</span><span></span></div>
              <div className="flex justify-between"><span>Taxes — {gstPct(gstRate)} GST</span><span data-testid="bd-gst">{inr(gstAmount)}</span></div>
              <div className="flex justify-between"><span>Ixitrip convenience fee</span><span data-testid="bd-convenience">{inr(CONVENIENCE_FEE)}</span></div>
              <div className="flex justify-between"><span>GST on convenience fee — {gstPct(CONVENIENCE_FEE_GST_RATE)}</span><span data-testid="bd-convenience-gst">{inr(CONVENIENCE_FEE_GST)}</span></div>
            </div>
            <hr className="border-[#EBEBEB] my-4" />
            <div className="flex justify-between font-display font-bold text-lg">
              <span>Total amount to be paid</span><span data-testid="booking-total">{inr(total)}</span>
            </div>
            <div className="mt-4 rounded-xl bg-[#FFF2F4] p-3 text-xs text-[#222] flex items-start gap-2">
              <BadgePercent className="w-4 h-4 text-[#FF385C] shrink-0" />
              <span><strong>Save 30%</strong> vs other OTAs — that's ~<strong>{inr(otaSaving)}</strong> on this stay (zero hotel commission).</span>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-[#717171]">
              <CalIcon className="w-3.5 h-3.5" /> Free cancellation up to 24h before check-in
            </div>
          </div>
        </aside>
      </div>

      <CheckoutAuthDialog
        open={authOpen}
        onOpenChange={setAuthOpen}
        onAuthed={() => { setAuthOpen(false); doCreateBooking(); }}
        nextPath={`/book/${hotelId}/${roomId}`}
        defaultEmail={guestEmail}
        defaultName={guestName}
      />
    </div>
  );
}
